/**
 * SecureStorage Utility
 *
 * Enterprise-grade secure storage for JWT tokens with encryption,
 * cross-tab synchronization, and automatic cleanup.
 *
 * Features:
 * - AES-256 encryption for sensitive data
 * - Cross-tab synchronization via storage events
 * - Automatic token expiry cleanup
 * - Secure key derivation
 * - Memory cleanup on logout
 */

import CryptoJS from 'crypto-js';

class SecureStorage {
    constructor() {
        this.storageKey = 'medinsight_secure_storage';
        this.secretKey = this.generateSecretKey();
        this.listeners = new Set();

        // Initialize storage event listener for cross-tab sync
        this.initCrossTabSync();

        // Cleanup expired tokens on initialization
        this.cleanupExpiredTokens();
    }

    /**
     * Generate or retrieve encryption key
     * Uses a combination of localStorage and derived key for consistency across sessions
     */
    generateSecretKey() {
        let key = localStorage.getItem('medinsight_master_key');

        if (!key) {
            // Generate a new master key only if none exists
            key = CryptoJS.lib.WordArray.random(256/8).toString();
            localStorage.setItem('medinsight_master_key', key);
            console.log('🔑 Generated new master encryption key');
        }

        // Derive key using PBKDF2 with a fixed salt for consistency across tabs and sessions
        const salt = 'medinsight_2025_salt';
        return CryptoJS.PBKDF2(key, salt, {
            keySize: 256/32,
            iterations: 1000
        }).toString();
    }

    /**
     * Encrypt data using AES-256
     */
    encrypt(data) {
        try {
            const jsonString = JSON.stringify(data);
            const encrypted = CryptoJS.AES.encrypt(jsonString, this.secretKey).toString();
            return encrypted;
        } catch (error) {
            console.error('Encryption failed:', error);
            throw new Error('Failed to encrypt data');
        }
    }

    /**
     * Decrypt data using AES-256
     */
    decrypt(encryptedData) {
        try {
            const decrypted = CryptoJS.AES.decrypt(encryptedData, this.secretKey);
            const jsonString = decrypted.toString(CryptoJS.enc.Utf8);

            // Check if decryption resulted in valid UTF-8 string
            if (!jsonString || jsonString.length === 0) {
                console.warn('🔓 Decryption resulted in empty string - possible key mismatch');
                return null;
            }

            const parsed = JSON.parse(jsonString);
            return parsed;
        } catch (error) {
            console.warn('🔓 Decryption failed (likely due to key change):', error.message);

            // If decryption fails, it's probably due to a key change
            // Clear corrupted data and start fresh
            this.handleDecryptionFailure();
            return null;
        }
    }

    /**
     * Handle decryption failure by clearing corrupted data
     */
    handleDecryptionFailure() {
        console.warn('🧹 Clearing corrupted encrypted data due to decryption failure');
        try {
            localStorage.removeItem(this.storageKey);
        } catch (error) {
            console.error('Failed to clear corrupted data:', error);
        }
    }

    /**
     * Get the current secure storage data
     */
    getStorageData() {
        try {
            const encryptedData = localStorage.getItem(this.storageKey);
            if (!encryptedData) {
                return {};
            }

            const decrypted = this.decrypt(encryptedData);
            return decrypted || {};
        } catch (error) {
            console.error('Failed to get storage data:', error);
            return {};
        }
    }

    /**
     * Save data to secure storage
     */
    setStorageData(data) {
        try {
            const encrypted = this.encrypt(data);
            localStorage.setItem(this.storageKey, encrypted);

            // Notify other tabs of the change
            this.notifyStorageChange();
        } catch (error) {
            console.error('Failed to save storage data:', error);
            throw error;
        }
    }

    /**
     * Store a token securely with metadata
     */
    setToken(key, token, expiresAt = null) {
        const storageData = this.getStorageData();

        const tokenData = {
            value: token,
            timestamp: Date.now(),
            expiresAt: expiresAt,
            lastAccessed: Date.now()
        };

        storageData[key] = tokenData;
        this.setStorageData(storageData);
    }

    /**
     * Retrieve a token securely
     */
    getToken(key) {
        const storageData = this.getStorageData();
        const tokenData = storageData[key];

        if (!tokenData) {
            return null;
        }

        // Check if token has expired
        if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
            this.removeToken(key);
            return null;
        }

        // Update last accessed time
        tokenData.lastAccessed = Date.now();
        storageData[key] = tokenData;
        this.setStorageData(storageData);

        return tokenData.value;
    }

    /**
     * Remove a token
     */
    removeToken(key) {
        const storageData = this.getStorageData();
        delete storageData[key];
        this.setStorageData(storageData);
    }

    /**
     * Get all stored token keys
     */
    getTokenKeys() {
        const storageData = this.getStorageData();
        return Object.keys(storageData);
    }

    /**
     * Check if a token exists and is valid
     */
    hasValidToken(key) {
        const token = this.getToken(key);
        return token !== null;
    }

    /**
     * Get token metadata without returning the token value
     */
    getTokenMetadata(key) {
        const storageData = this.getStorageData();
        const tokenData = storageData[key];

        if (!tokenData) {
            return null;
        }

        return {
            timestamp: tokenData.timestamp,
            expiresAt: tokenData.expiresAt,
            lastAccessed: tokenData.lastAccessed,
            isExpired: tokenData.expiresAt ? Date.now() > tokenData.expiresAt : false
        };
    }

    /**
     * Clear all tokens (logout)
     */
    clearAll() {
        try {
            localStorage.removeItem(this.storageKey);
            localStorage.removeItem('medinsight_master_key');
            sessionStorage.removeItem('medinsight_session_key');

            // Clear from memory and regenerate key
            this.secretKey = this.generateSecretKey();

            // Notify other tabs
            this.notifyStorageChange();

            console.log('🧹 Cleared all secure storage data');
        } catch (error) {
            console.error('Failed to clear storage:', error);
        }
    }

    /**
     * Cleanup expired tokens
     */
    cleanupExpiredTokens() {
        const storageData = this.getStorageData();
        let hasChanges = false;

        Object.keys(storageData).forEach(key => {
            const tokenData = storageData[key];
            if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
                delete storageData[key];
                hasChanges = true;
            }
        });

        if (hasChanges) {
            this.setStorageData(storageData);
        }
    }

    /**
     * Initialize cross-tab synchronization
     */
    initCrossTabSync() {
        // Listen for storage changes from other tabs
        window.addEventListener('storage', (event) => {
            if (event.key === this.storageKey) {
                // Notify listeners of storage change
                this.listeners.forEach(listener => {
                    try {
                        listener({
                            type: 'storage_change',
                            key: event.key,
                            newValue: event.newValue,
                            oldValue: event.oldValue
                        });
                    } catch (error) {
                        console.error('Storage listener error:', error);
                    }
                });
            }
        });

        // Listen for custom events for same-tab notifications
        window.addEventListener('medinsight_storage_change', (event) => {
            this.listeners.forEach(listener => {
                try {
                    listener({
                        type: 'storage_change',
                        source: 'same_tab',
                        detail: event.detail
                    });
                } catch (error) {
                    console.error('Storage listener error:', error);
                }
            });
        });
    }

    /**
     * Notify other tabs and same tab listeners of storage changes
     */
    notifyStorageChange() {
        // Dispatch custom event for same-tab listeners
        const event = new CustomEvent('medinsight_storage_change', {
            detail: {
                timestamp: Date.now(),
                keys: this.getTokenKeys()
            }
        });
        window.dispatchEvent(event);
    }

    /**
     * Add a listener for storage changes
     */
    addListener(callback) {
        this.listeners.add(callback);

        // Return unsubscribe function
        return () => {
            this.listeners.delete(callback);
        };
    }

    /**
     * Remove a storage change listener
     */
    removeListener(callback) {
        this.listeners.delete(callback);
    }

    /**
     * Get storage statistics
     */
    getStorageStats() {
        const storageData = this.getStorageData();
        const keys = Object.keys(storageData);

        let totalSize = 0;
        let expiredCount = 0;
        let validCount = 0;

        keys.forEach(key => {
            const tokenData = storageData[key];
            totalSize += JSON.stringify(tokenData).length;

            if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
                expiredCount++;
            } else {
                validCount++;
            }
        });

        return {
            totalTokens: keys.length,
            validTokens: validCount,
            expiredTokens: expiredCount,
            estimatedSize: totalSize,
            lastCleanup: this.lastCleanup || 'Never'
        };
    }

    /**
     * Migrate existing tokens from old storage keys
     * This method helps transition from the current storage implementation
     */
    migrateFromLegacyStorage() {
        const legacyKeys = [
            'access_token',
            'refresh_token',
            'admin_token',
            'user_info',
            'token_expiry'
        ];

        let migrated = 0;

        legacyKeys.forEach(key => {
            const value = localStorage.getItem(key);
            if (value) {
                try {
                    // Determine if it's JSON or plain text
                    let tokenValue;
                    try {
                        tokenValue = JSON.parse(value);
                    } catch {
                        tokenValue = value;
                    }

                    // Set with appropriate expiry if available
                    let expiresAt = null;
                    if (key === 'token_expiry') {
                        expiresAt = parseInt(value);
                    }

                    this.setToken(key, tokenValue, expiresAt);

                    // Remove from legacy storage
                    localStorage.removeItem(key);
                    migrated++;
                } catch (error) {
                    console.error(`Failed to migrate ${key}:`, error);
                }
            }
        });

        console.log(`Migrated ${migrated} tokens from legacy storage`);
        return migrated;
    }

    /**
     * Recovery function to handle corrupted storage
     * Call this when decryption consistently fails
     */
    recoverFromCorruption() {
        console.warn('🛠️ Starting storage corruption recovery...');

        try {
            // Step 1: Clear all secure storage
            localStorage.removeItem(this.storageKey);
            localStorage.removeItem('medinsight_master_key');
            sessionStorage.removeItem('medinsight_session_key');

            // Step 2: Regenerate encryption key
            this.secretKey = this.generateSecretKey();

            // Step 3: Try to migrate any legacy tokens that might still be valid
            const migrated = this.migrateFromLegacyStorage();

            console.log(`✅ Storage recovery complete. Migrated ${migrated} legacy tokens.`);

            // Step 4: Notify user
            if (window.confirm('Storage corruption detected and fixed. The page will reload to apply changes.')) {
                window.location.reload();
            }

            return true;
        } catch (error) {
            console.error('❌ Storage recovery failed:', error);
            return false;
        }
    }
}

// Create singleton instance
const secureStorage = new SecureStorage();

export default secureStorage;